import { ApplicationRef, inject, Injectable, NgZone } from '@angular/core'
import { BehaviorSubject, delay, interval, Subscription } from 'rxjs'
import { filter, first, mergeMap, switchMap, tap } from 'rxjs/operators'

import { MessageService } from 'primeng/api'
import { get } from 'lodash-es'

import { newUser, User } from '@app/interfaces/user'
import { LongRunningJobMessage, TranslatedLongRunningJob } from '@app/components/long-running-jobs/long-running-job'
import { LongRunningJobsApi } from '@app/components/long-running-jobs/long-running-jobs.api'
import { LongRunningJobStatus } from '@app/generated/api/model'
import type { LongRunningJob } from '@app/generated/api/model'

import { UserService } from './user.service'

@Injectable()
export class LongRunningJobService {
  private readonly messageService = inject(MessageService)
  private readonly zone = inject(NgZone)
  private readonly longRunningJobsApi = inject(LongRunningJobsApi)
  private readonly userService = inject(UserService)
  private readonly ngZone = inject(NgZone)
  public readonly appRef = inject(ApplicationRef)

  private _jobs: TranslatedLongRunningJob[] = []
  private jobsList = new BehaviorSubject<TranslatedLongRunningJob[]>([])

  private _broadcastMessage = new BehaviorSubject<string>('')
  broadcastMessage$ = this._broadcastMessage.asObservable()

  private user: User = newUser()
  public userSubscription!: Subscription

  constructor () {
    // listen for the user to login, and then launch the LRJ fetch / poll machine
    this.userSubscription = this.userService.getUser()
      .pipe(
        tap(user => {
          this.user = user // Set user in service.
        }),
        filter(user => !user.anonymous), // Filter out anonymous users.
        filter(_ => this.userService.hasPermissionIn(['view_longrunningjob'])), // Filter out users without proper permission.
        mergeMap(_ => this.appRef.isStable), // Listen to app status
        first(stable => stable), // Wait for the first time the app is stable
        // app is stable, user is logged in, fetch their existing Long Running Jobs to initialise our list.
        delay(3000), // we delay this by a few seconds because it is not priority, and we allow other calls to pass first
        tap(() => this.resetLongRunningJobs()),
        // launch poll checker every 2 seconds which will poll individual LRJs once they are started
        switchMap(() => interval(2000)),
      )
      .subscribe(() => this.ngZone.run(() => this.pollJobs()))
  }

  /**
   * Broadcast a message with payload job_type:job_status to potential consumers for this job
   */
  broadCastMessage (job: TranslatedLongRunningJob) {
    // perform this in zone to ensure that if there is a table to refresh the new data is rendered.
    this.zone.run(() => {
      this._broadcastMessage.next(`${job.type}:${job.status}`)
    })
  }

  /**
   * Reset the current list of LRJs and re-fetch the total list from the server.
   */
  resetLongRunningJobs () {
    if (this.user.anonymous) return

    this.longRunningJobsApi.list({}).subscribe((response) => {
      this._jobs = []
      response.results.forEach(result => {
        this.addJob(result)
      })
      this.jobsList.next(this._jobs)
    })
  }

  /**
   * Poll a specific job to track its progression.
   *
   * Broadcasts a message when done.
   */
  pollJob (job: TranslatedLongRunningJob) {
    this.longRunningJobsApi.retrieve(job.code).subscribe(response => {
      const updatedJob: TranslatedLongRunningJob = {
        ...job,
        progress: response.progress,
        status: response.status,
        message: toLongRunningJobMessage(response.message),
      }
      // perform this in zone to ensure the updated data is rendered
      this.zone.run(() => {
        const index = this._jobs.indexOf(job)
        if (index !== -1) this._jobs[index] = updatedJob
        this.jobsList.next([...this._jobs])
        if (updatedJob.message?.warning) {
          this.messageService.add({ severity: 'warn', summary: 'Warning', detail: updatedJob.message.warning, sticky: true })
        }
      })
      // if the job was success, broadcast the message to maybe trigger a table refresh, for example (e.g. crm list)
      if (updatedJob.status === LongRunningJobStatus.DONE) {
        this.broadCastMessage(updatedJob)
      }
    })
  }

  /**
   * Return the translated human-readable string for a LRJ type
   */
  getTranslatedTypeName (typeCode: string): string {
    // Define all possible long running job types here.
    // They will be automatically translatable via angular i18n.
    // TODO: this list should be added to a global constants page probably.
    const typeCodes: { [code: string]: string } = {
      import_complaints: $localize`:@@long-running-jobs-import-complaints:Import complaints`,
      import_financial_sheet_version: $localize`:@@long-running-jobs-import-financial-sheet-version:Import financial sheet`,
      import_procurement_plan: $localize`:@@long-running-jobs-import-procurement-plan:Import procurement plan`,
      import_staff: $localize`:@@long-running-jobs-import-staff:Import staff`,
      import_screening_staff: $localize`:@@long-running-jobs-import-screening-staff:Import staff for screening`,
      import_talencia_users: $localize`:@@long-running-jobs-import-talencia-users:Import Talencia users`,
      export_archive_documents: $localize`:@@long-running-jobs-export-archive-documents:Export documents`,
      export_balzac_books: $localize`:@@long-running-jobs-export-balzac-books:Export balzac books`,
      export_complaints: $localize`:@@long-running-jobs-export-complaints:Export complaints`,
      export_deliverables: $localize`:@@long-running-jobs-export-deliverables:Export deliverables`,
      export_donors: $localize`:@@long-running-jobs-export-donors:Export donors`,
      export_external_audits: $localize`:@@long-running-jobs-export-external-audits:Export external audits`,
      export_grasp_entries: $localize`:@@long-running-jobs-export-grasp-entries:Export GRASP`,
      export_incidents: $localize`:@@long-running-jobs-export-incidents:Export incidents`,
      export_premises: $localize`:@@long-running-jobs-export-premises:Export premises`,
      export_projects: $localize`:@@long-running-jobs-export-projects:Export projects`,
      export_partners: $localize`:@@long-running-jobs-export-partners:Export partners`,
      export_screening_entities: $localize`:@@long-running-jobs-export-screening-entities:Export screening entities`,
      export_suppliers: $localize`:@@long-running-jobs-export-suppliers:Export suppliers`,
      export_talencia_users: $localize`:@@long-running-jobs-export-talencia-users:Export Talencia users`,
      validate_screening_entities: $localize`:@@long-running-jobs-validate_screening_entities:Validate screening entities`,
    }

    // Return the translated version, falling back to the type code if it wasn't supplied above.
    return get(typeCodes, typeCode, typeCode)
  }

  /**
   * Add a new job to the user's current list, presumably following its creation.
   */
  addJob (djangoResponse: LongRunningJob) {
    const job = toLongRunningJob(djangoResponse, (type) => this.getTranslatedTypeName(type))
    this._jobs.push(job)
    this.jobsList.next(this._jobs)
  }

  /**
   * Remove a job from the global list, probably coz the user clicked the 'close' button.
   */
  removeJob (job: TranslatedLongRunningJob) {
    this._jobs.splice(this._jobs.indexOf(job), 1)
    this.jobsList.next(this._jobs)
  }

  getJobs () {
    return this.jobsList.asObservable()
  }

  /**
   * loop over all running jobs and call their individual poll.
   */
  private pollJobs () {
    this._jobs.forEach((job) => {
      if (job.status === LongRunningJobStatus.WAITING || job.status === LongRunningJobStatus.RUNNING) {
        this.pollJob(job)
      }
    })
  }
}

function toLongRunningJob (
  djangoResponse: LongRunningJob,
  getTranslatedTypeName: (type: string) => string,
): TranslatedLongRunningJob {
  return {
    code: djangoResponse.code,
    type: djangoResponse.type,
    progress: djangoResponse.progress,
    status: djangoResponse.status,
    message: toLongRunningJobMessage(djangoResponse.message),
    translatedTypeName: getTranslatedTypeName(djangoResponse.type),
  }
}

function toLongRunningJobMessage (message: unknown): LongRunningJobMessage | null {
  if (!message || typeof message !== 'object' || Array.isArray(message)) return null

  const rawMessage = message as Record<string, unknown>
  return {
    attachUuid: stringValue(rawMessage.attach_uuid),
    detail: stringValue(rawMessage.detail),
    message: stringValue(rawMessage.message),
    warning: stringValue(rawMessage.warning),
  }
}

function stringValue (value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}
